import { Moon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@/shared/theme'
import { cn } from '@/shared/ui/cn'

/**
 * The dark-mode switch — the footer and the mobile sheet (owner ruling,
 * 1 October 2026: light by default, dark on request, remembered). The account
 * menu carries the same switch as a `DropdownMenuCheckboxItem`.
 *
 * A quiet text button in the language switcher's dress: the brand reference's
 * header is deliberately sparse, so the switch lives at the foot of the page
 * where anyone can find it and nobody trips over it.
 *
 * ⚠️ **The name never changes; `aria-pressed` carries the state.** A toggle
 * button whose label flips between "Dark mode" and "Light mode" *as well as*
 * reporting pressed is contradictory — a screen reader announces "Light mode,
 * pressed" and the user cannot tell which is on. So it is always "Dark mode",
 * pressed or not. The sighted state is the page itself, plus the moon filling
 * in: a shape change, not a colour one.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { theme, toggleTheme } = useTheme()
  const dark = theme === 'dark'

  return (
    <button
      type="button"
      aria-pressed={dark}
      onClick={toggleTheme}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[5px] px-2 py-1.5 transition-colors hover:bg-hover hover:text-brand-fg',
        className,
      )}
    >
      <Moon aria-hidden="true" className={cn('size-3.5 shrink-0', dark && 'fill-current')} />
      {t('theme.dark')}
    </button>
  )
}
